import { motion, AnimatePresence } from 'framer-motion';
import type { ChatDocument, ChatMessageDocument } from '@models/chat';
import type { UserDocument } from '@models/user';
import { deleteMessage } from '@util/firestore/messages';
import { useEffect, useRef } from 'react';

interface MessageMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
  chat: ChatDocument;
  message: ChatMessageDocument;
  isOutbound: boolean;
  onCopyMessage: () => void;
  userDoc: UserDocument;
}

interface MenuPosition {
  x: number;
  y: number;
  transformOrigin: string;
}

export default function MessageMenu({
  isOpen,
  onClose,
  position,
  chat,
  message,
  isOutbound,
  onCopyMessage,
  userDoc,
}: MessageMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate smart positioning to avoid viewport edges
  const getSmartPosition = (): MenuPosition => {
    const menuWidth = 180; // estimated menu width
    const menuHeight = 120; // estimated menu height
    const padding = 16; // padding from viewport edges

    const viewport = {
      width: typeof window !== 'undefined' ? window.innerWidth : 1200,
      height: typeof window !== 'undefined' ? window.innerHeight : 800,
    };

    let x = position.x;
    let y = position.y;
    let transformOrigin = 'top left';

    // Adjust horizontal position
    if (x + menuWidth + padding > viewport.width) {
      x = position.x - menuWidth;
      transformOrigin = 'top right';
    }

    // Adjust vertical position
    if (y + menuHeight + padding > viewport.height) {
      y = position.y - menuHeight;
      transformOrigin = transformOrigin.replace('top', 'bottom');
    }

    return { x, y, transformOrigin };
  };

  const smartPosition = getSmartPosition();

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleDeleteMessage = () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessage(chat.id, message);
      onClose();
    }
  };

  const handleCopyMessage = () => {
    onCopyMessage();
    onClose();
  };

  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          className="fixed z-50"
          style={{
            left: smartPosition.x,
            top: smartPosition.y,
            transformOrigin: smartPosition.transformOrigin,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: 1,
            opacity: 1,
            transition: {
              type: 'spring',
              stiffness: 500,
              damping: 30,
              mass: 1,
            },
          }}
          exit={{
            scale: 0,
            opacity: 0,
            transition: {
              duration: 0.2,
              ease: 'easeInOut',
            },
          }}
        >
          <div
            className={`relative min-w-[16rem] rounded-[1.5rem] border border-gray-100 bg-white p-[1rem] shadow-lg ${
              isOutbound
                ? 'before:absolute before:-right-[0.6rem] before:top-[1rem] before:h-0 before:w-0 before:border-b-[0.8rem] before:border-l-[0.8rem] before:border-t-[0.8rem] before:border-b-transparent before:border-l-white before:border-t-transparent'
                : 'before:absolute before:-left-[0.6rem] before:top-[1rem] before:h-0 before:w-0 before:border-b-[0.8rem] before:border-r-[0.8rem] before:border-t-[0.8rem] before:border-b-transparent before:border-r-white before:border-t-transparent'
            }`}
          >
            <div className="flex flex-col gap-[0.4rem]">
              {isLoggedInAsSupport && (
                <motion.button
                  type="button"
                  className="cursor-pointer rounded-[0.8rem] px-[1.2rem] py-[0.8rem] text-left text-[1.4rem] text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={handleDeleteMessage}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Delete message
                </motion.button>
              )}
              <motion.button
                type="button"
                className="cursor-pointer rounded-[0.8rem] px-[1.2rem] py-[0.8rem] text-left text-[1.4rem] text-gray-700 transition-colors hover:bg-gray-50"
                onClick={handleCopyMessage}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Copy message
              </motion.button>
              <motion.button
                type="button"
                className="cursor-pointer rounded-[0.8rem] px-[1.2rem] py-[0.8rem] text-left text-[1.4rem] text-gray-500 transition-colors hover:bg-gray-50"
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
